import { ChevronDown, ChevronRight, Landmark, Lock, ShieldAlert } from 'lucide-react'
import { useState } from 'react'

import type { LedgerGlAccountClass, LedgerGlAccountNode } from '@/api/ledger-types'
import { session } from '@/api/session'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { formatBusinessDate } from '@/lib/datetime'
import { cn } from '@/lib/utils'

import { CreateGlAccountAction, GlAccountMaintenanceActions } from './chart-maintenance'
import {
  GL_ACCOUNT_CLASS_LABEL,
  GL_ACCOUNT_STATUS_TONE,
  NORMAL_BALANCE_LABEL,
  today,
} from './constants'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'
import { useChartOfAccounts, useGlAccount } from './queries'
import { useDebounced } from './use-debounced'

/**
 * The chart of accounts.
 *
 * Read as a tree, because that is what it is: a heading groups its children and takes no postings,
 * and only the leaves at the bottom of a branch accept an entry. Flattening it into a paged list
 * would lose the one relationship a reader is actually using — where an account sits.
 *
 * The chart is **institution-wide, not branch-scoped**. There is one of it, every branch posts into
 * the same accounts, and blocking one stops every branch. That is stated on the screen because the
 * rest of this application is branch-scoped and the assumption carries over wrongly.
 *
 * Maintenance lives in `chart-maintenance.tsx` — create, amend, block, unblock, archive, all
 * countersigned under `ledger.account.manage`. This half only reads.
 *
 * Reading works at any width.
 */

const CLASSES: LedgerGlAccountClass[] = ['Asset', 'Liability', 'Equity', 'Income', 'Expense']

const ANY = '__any__'

export function ChartOfAccountsSection() {
  const permissions = useLedgerPermissions()

  const [search, setSearch] = useState('')
  const [accountClass, setAccountClass] = useState<LedgerGlAccountClass | null>(null)
  const [postableOnly, setPostableOnly] = useState(false)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [selectedCode, setSelectedCode] = useState<string | null>(null)
  const [asOf, setAsOf] = useState(today)

  // Settled before it reaches the query key — see `use-debounced.ts`.
  const settledSearch = useDebounced(search.trim())

  const chart = useChartOfAccounts(
    { search: settledSearch, accountClass, postableOnly, includeArchived },
    permissions.canViewAccounts,
  )

  if (!permissions.canViewAccounts) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
        <ShieldAlert className="size-6 text-warning-text" aria-hidden />
        <p className="text-sm font-medium text-heading">The chart of accounts is restricted</p>
        <p className="max-w-lg text-xs text-muted">
          Reading it needs <span className="font-mono">{LEDGER_PERMISSIONS.accountView}</span>, which
          your role does not hold.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Alert tone="info">
        <Landmark aria-hidden />
        <div className="space-y-1">
          <AlertTitle>One chart, every branch</AlertTitle>
          <AlertDescription>
            The chart is institution-wide rather than branch-scoped — every branch posts into these
            same accounts. Only postable accounts take entries; a heading exists to group the ones
            beneath it and refuses a posting outright.
          </AlertDescription>
        </div>
      </Alert>

      <div className="grid gap-3 rounded-card border border-border-subtle bg-surface p-3 lg:grid-cols-4">
        <div className="space-y-1.5 lg:col-span-2">
          <Label htmlFor="chart-search">Search</Label>
          <Input
            id="chart-search"
            value={search}
            autoComplete="off"
            placeholder="Code or name"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="chart-class">Class</Label>
          <Select
            value={accountClass ?? ANY}
            onValueChange={(next) =>
              setAccountClass(next === ANY ? null : (next as LedgerGlAccountClass))
            }
          >
            <SelectTrigger id="chart-class">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Every class</SelectItem>
              {CLASSES.map((candidate) => (
                <SelectItem key={candidate} value={candidate}>
                  {GL_ACCOUNT_CLASS_LABEL[candidate]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col justify-end gap-2">
          <label className="flex items-center gap-2 text-sm text-body max-lg:min-h-11">
            <Checkbox
              checked={postableOnly}
              onCheckedChange={(next) => setPostableOnly(next === true)}
            />
            Postable accounts only
          </label>
          <label className="flex items-center gap-2 text-sm text-body max-lg:min-h-11">
            <Checkbox
              checked={includeArchived}
              onCheckedChange={(next) => setIncludeArchived(next === true)}
            />
            Include archived
          </label>
        </div>
      </div>

      {chart.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : chart.isError ? (
        <ErrorSurface
          error={chart.error}
          onRetry={() => void chart.refetch()}
          title="The chart of accounts could not be read"
        />
      ) : chart.data.roots.length === 0 ? (
        <Card>
          <CardContent>
            <p className="py-8 text-center text-sm text-body">
              {settledSearch === '' && accountClass === null
                ? 'The chart of accounts is empty. Nothing can be posted anywhere until it is loaded, which is a setup task rather than something this screen can do.'
                : 'No account matches those filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
          <Card>
            <CardHeader>
              <div className="min-w-0">
                <CardTitle>Accounts</CardTitle>
                <p className="text-xs text-muted">
                  <span className="numeric tabular-nums">{chart.data.accountCount}</span> in the chart,{' '}
                  <span className="numeric tabular-nums">{chart.data.postableCount}</span> postable
                </p>
              </div>
              <CreateGlAccountAction roots={chart.data.roots} />
            </CardHeader>
            <CardContent className="p-2">
              <ul className="space-y-0.5">
                {chart.data.roots.map((node) => (
                  <AccountBranch
                    key={node.publicId}
                    node={node}
                    depth={0}
                    selectedCode={selectedCode}
                    onSelect={setSelectedCode}
                  />
                ))}
              </ul>
            </CardContent>
          </Card>

          <AccountDetailPanel code={selectedCode} asOf={asOf} onAsOfChange={setAsOf} />
        </div>
      )}
    </div>
  )
}

/**
 * One account and everything under it.
 *
 * Expanded by default down to the third level and collapsed below it: the top of a chart is the
 * structure somebody is orienting by, and the bottom is where the hundreds of leaves live.
 */
function AccountBranch({
  node,
  depth,
  selectedCode,
  onSelect,
}: {
  node: LedgerGlAccountNode
  depth: number
  selectedCode: string | null
  onSelect: (code: string) => void
}) {
  const [expanded, setExpanded] = useState(depth < 2)
  const hasChildren = node.children.length > 0
  const selected = node.code === selectedCode

  return (
    <li>
      <div
        className={cn(
          'flex items-center gap-1 rounded-control px-1 py-1 transition-colors hover:bg-surface-hover',
          selected && 'bg-brand-fill-subtle',
        )}
        style={{ paddingLeft: `${String(depth * 0.85 + 0.25)}rem` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${node.code}` : `Expand ${node.code}`}
            className="flex size-5 shrink-0 items-center justify-center rounded-sm text-muted hover:text-heading max-lg:size-8"
          >
            {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          </button>
        ) : (
          <span className="size-5 shrink-0 max-lg:size-8" aria-hidden />
        )}

        <button
          type="button"
          onClick={() => onSelect(node.code)}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-left max-lg:min-h-11"
        >
          <span className="font-mono text-xs text-heading">{node.code}</span>
          <span className="min-w-0 flex-1 truncate text-sm text-body">{node.name}</span>
          {node.isPostable ? null : (
            <Badge tone="neutral" className="shrink-0">
              Heading
            </Badge>
          )}
          {node.status === 'Active' ? null : (
            <Badge tone={GL_ACCOUNT_STATUS_TONE[node.status]} className="shrink-0">
              {node.status}
            </Badge>
          )}
          {node.isSystemAccount ? (
            <Lock className="size-3 shrink-0 text-muted" aria-label="System account" />
          ) : null}
        </button>
      </div>

      {hasChildren && expanded ? (
        <ul className="space-y-0.5">
          {node.children.map((child) => (
            <AccountBranch
              key={child.publicId}
              node={child}
              depth={depth + 1}
              selectedCode={selectedCode}
              onSelect={onSelect}
            />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

/**
 * One account, read on its own.
 *
 * The balance is asked for at the session's branch and as at a date, because that is what the
 * endpoint takes — and a null answer is rendered as an em dash rather than as zero. A heading holds
 * no balance and an account never posted to holds none either; showing `0.00` for both would state
 * a fact the ledger has not asserted.
 */
function AccountDetailPanel({
  code,
  asOf,
  onAsOfChange,
}: {
  code: string | null
  asOf: string
  onAsOfChange: (next: string) => void
}) {
  const branchId = session.getBranchId()
  const query = useGlAccount(code, { branchId, asOf })

  if (code === null) {
    return (
      <Card>
        <CardContent>
          <p className="py-8 text-center text-sm text-muted">
            Choose an account to read its detail, its place in the chart, and its balance.
          </p>
        </CardContent>
      </Card>
    )
  }

  if (query.isPending) {
    return <Skeleton className="h-80 w-full" />
  }

  if (query.isError) {
    return (
      <ErrorSurface
        error={query.error}
        onRetry={() => void query.refetch()}
        title="That account could not be read"
      />
    )
  }

  const { account, ancestorCodes, balanceMinorUnits } = query.data

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0 space-y-1">
          <CardTitle className="font-mono">{account.code}</CardTitle>
          <p className="text-sm text-body">{account.name}</p>
        </div>
        <Badge tone={GL_ACCOUNT_STATUS_TONE[account.status]}>{account.status}</Badge>
      </CardHeader>

      <CardContent className="space-y-4">
        {ancestorCodes.length > 0 ? (
          <p className="font-mono text-2xs text-muted">{ancestorCodes.join(' › ')} › {account.code}</p>
        ) : (
          <p className="text-2xs text-muted">A root of the chart.</p>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="account-as-of">Balance as at</Label>
          <Input
            id="account-as-of"
            type="date"
            value={asOf}
            max={today()}
            onChange={(event) => onAsOfChange(event.target.value)}
          />
        </div>

        <div className="rounded-control border border-border-subtle bg-surface-muted p-3">
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">
            Balance {query.data.branchId === null ? '(institution)' : `(branch ${query.data.branchId})`}
          </p>
          <MoneyDisplay
            value={balanceMinorUnits}
            currency={account.currencyCode}
            emphasis="strong"
            colourNegative
            className="mt-0.5 text-base"
          />
          <p className="mt-1 text-2xs text-muted">
            {balanceMinorUnits === null
              ? 'The ledger holds no balance for this account on that date. That is not zero — a heading has none by definition, and neither does an account that has never been posted to.'
              : `As at ${formatBusinessDate(query.data.asOf)}. ${NORMAL_BALANCE_LABEL[account.normalBalance]} is the side that increases this account.`}
          </p>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Detail label="Class" value={GL_ACCOUNT_CLASS_LABEL[account.class]} />
          <Detail label="Normal balance" value={NORMAL_BALANCE_LABEL[account.normalBalance]} />
          <Detail label="Takes postings" value={account.isPostable ? 'Yes' : 'No — heading'} />
          <Detail label="Currency" value={account.currencyCode} />
          <Detail
            label="BoG return line"
            value={account.boGReturnLineCode ?? 'Untagged'}
            note={
              account.boGReturnLineCode === null
                ? 'It rolls into no return line, so it is absent from the trial balance’s regulatory roll-up.'
                : undefined
            }
          />
          <Detail
            label="System account"
            value={account.isSystemAccount ? 'Yes' : 'No'}
            note={
              account.isSystemAccount
                ? 'A module posts to this one by name. Re-purposing it by hand would break that posting.'
                : undefined
            }
          />
        </dl>

        {account.description ? (
          <p className="border-t border-border-subtle pt-3 text-xs text-body">{account.description}</p>
        ) : null}

        <GlAccountMaintenanceActions account={account} />

        {account.children.length > 0 ? (
          <div className="border-t border-border-subtle pt-3">
            <p className="text-2xs font-semibold tracking-wide text-muted uppercase">
              Directly beneath it
            </p>
            <ul className="mt-1 space-y-0.5">
              {account.children.map((child) => (
                <li key={child.publicId} className="text-xs">
                  <span className="font-mono text-heading">{child.code}</span>{' '}
                  <span className="text-body">{child.name}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function Detail({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm text-heading">{value}</dd>
      {note ? <p className="mt-0.5 text-2xs text-muted">{note}</p> : null}
    </div>
  )
}
